import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Copy, CheckCircle, Clock, QrCode as QrCodeIcon, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { TribopayPackage } from "@/constants/tribopayPackages";
import { usePixTransaction } from "@/hooks/usePixTransaction";
import { Skeleton } from "@/components/ui/skeleton";
import { QRCodeSVG } from "qrcode.react";
interface PixCheckoutProps {
  isOpen: boolean;
  onClose: () => void;
  package: TribopayPackage;
  customerData: {
    name: string;
    email: string;
    phone: string;
    document: string;
  };
}
export function PixCheckout({ isOpen, onClose, package: pkg, customerData }: PixCheckoutProps) {
  const [qrCode, setQrCode] = useState<string>("");
  const [pixCode, setPixCode] = useState<string>("");
  const [expiresAt, setExpiresAt] = useState<string>("");
  const [transactionHash, setTransactionHash] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { status } = usePixTransaction(transactionHash);

  // Ref para prevenir chamadas duplicadas (StrictMode)
  const isCreatingRef = useRef(false);

  useEffect(() => {
    if (isOpen && !transactionHash && !isCreatingRef.current) {
      isCreatingRef.current = true;
      createPixTransaction();
    }
  }, [isOpen, transactionHash]);
  useEffect(() => {
    if (!expiresAt) return;
    const calculateTimeRemaining = () => {
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();
      const remaining = Math.max(0, Math.floor((expiry - now) / 1000));
      setTimeRemaining(remaining);
    };
    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);
  useEffect(() => {
    if (status === "paid") {
      // Efeito sonoro de sucesso (opcional)
      const audio = new Audio(
        "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZUQ0PVqzn77BdGAg+ltryxnMnBSp+zO/aizsIGGS57OihUxELTKXh8bllHAU2jdXzzn4vBSF1xe7dkUEKE1yw6OyrWRUMSKHe8sFuIwU",
      );
      audio.play().catch(() => {});
      toast({
        title: "✅ Pagamento confirmado!",
        description: `${pkg.credits} créditos foram adicionados à sua conta.`,
      });
      setTimeout(() => {
        onClose();
        window.location.reload();
      }, 2000);
    }
  }, [status, pkg.credits, onClose, toast]);

  // Fechar automaticamente quando o cronômetro zerar
  useEffect(() => {
    if (timeRemaining === 0 && expiresAt && status !== "paid") {
      const timeout = setTimeout(() => {
        toast({
          title: "PIX Expirado",
          description: "O tempo para pagamento expirou. Gere um novo PIX.",
          variant: "destructive",
        });
        handleClose();
      }, 2000);
      
      return () => clearTimeout(timeout);
    }
  }, [timeRemaining, expiresAt, status]);
  const createPixTransaction = async () => {
    setLoading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");
      const { data, error } = await supabase.functions.invoke("create-tribopay-pix", {
        body: {
          packageId: pkg.id,
          productHash: pkg.productHash,
          customer: customerData,
        },
      });
      if (error) throw error;
      if (!data.success) {
        throw new Error(data.error || "Erro ao processar pagamento");
      }
      setQrCode(data.qrCode);
      setPixCode(data.pixCode);
      setExpiresAt(data.expiresAt);
      setTransactionHash(data.transactionHash);
    } catch (error: any) {
      console.error("Error creating PIX transaction:", error);
      toast({
        title: "Erro ao gerar PIX",
        description: error.message || "Tente novamente mais tarde.",
        variant: "destructive",
      });
      onClose();
    } finally {
      setLoading(false);
      isCreatingRef.current = false;
    }
  };
  const copyPixCode = () => {
    navigator.clipboard.writeText(pixCode);
    toast({
      title: "Código PIX copiado!",
      description: "Cole no app do seu banco para pagar.",
    });
  };
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };
  const handleClose = () => {
    if (status !== "paid") {
      setQrCode("");
      setPixCode("");
      setExpiresAt("");
      setTransactionHash(null);
      setTimeRemaining(0);
    }
    // Reset ref ao fechar para permitir nova criação
    isCreatingRef.current = false;
    onClose();
  };
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto p-0">
        {/* Botão fechar fixo para mobile */}
        <div className="sticky top-0 z-10 flex justify-end p-2 bg-background/95 backdrop-blur-sm border-b border-border/50">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="rounded-full h-8 w-8 p-0"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="px-6 pb-6">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-xl sm:text-2xl font-bold text-center">Pagamento via PIX</DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="space-y-3 py-4">
              <Skeleton className="h-48 sm:h-64 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : status === "paid" ? (
            <div className="py-8 text-center space-y-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-24 h-24 bg-green-500/20 rounded-full animate-ping" />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 bg-green-500/30 rounded-full animate-pulse" />
                </div>
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto relative animate-bounce" />
              </div>
              <h3 className="text-xl font-bold text-foreground animate-pulse">Pagamento Confirmado!</h3>
              <p className="text-muted-foreground">{pkg.credits} créditos foram adicionados à sua conta.</p>
              <div className="flex items-center justify-center gap-2 text-sm text-green-600">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">QR Code escaneado com sucesso!</span>
              </div>
            </div>
          ) : status === "expired" ? (
            <div className="py-8 text-center space-y-4">
              <Clock className="w-16 h-16 text-orange-500 mx-auto" />
              <h3 className="text-xl font-bold text-foreground">PIX Expirado</h3>
              <p className="text-muted-foreground">O tempo para pagamento expirou. Gere um novo PIX para continuar.</p>
              <Button onClick={handleClose}>Fechar</Button>
            </div>
          ) : (
            <div className="space-y-4 sm:space-y-6 py-2 sm:py-4">
              {/* Timer */}
              <Card
                className={`p-3 sm:p-4 ${timeRemaining <= 300 ? "bg-destructive/10 border-destructive/30" : "bg-accent/50"}`}
              >
                <div className="flex flex-col gap-1 sm:gap-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock
                        className={`w-4 h-4 sm:w-5 sm:h-5 ${timeRemaining <= 300 ? "text-destructive" : "text-primary"}`}
                      />
                      <span className="text-xs sm:text-sm font-medium">Tempo restante:</span>
                    </div>
                    <span
                      className={`text-base sm:text-lg font-bold ${timeRemaining <= 300 ? "text-destructive animate-pulse" : "text-primary"}`}
                    >
                      {formatTime(timeRemaining)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground text-center"></p>
                </div>
              </Card>

              {/* Package Info */}
              <div className="text-center space-y-0.5 sm:space-y-1">
                <h3 className="text-base sm:text-lg font-semibold">{pkg.name}</h3>
                <p className="text-xl sm:text-2xl font-bold text-primary-foreground">R$ {pkg.priceBRL}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">{pkg.credits} créditos</p>
              </div>

              {/* QR Code */}
              <Card className="p-4 sm:p-6 bg-white flex items-center justify-center">
                {pixCode ? (
                  <QRCodeSVG
                    value={pixCode}
                    size={256}
                    level="H"
                    includeMargin={true}
                    className="w-full h-auto max-w-[180px] sm:max-w-[256px]"
                  />
                ) : (
                  <div className="aspect-square flex items-center justify-center">
                    <QrCodeIcon className="w-12 h-12 sm:w-16 sm:h-16 text-muted-foreground" />
                  </div>
                )}
              </Card>

              {/* PIX Code */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-center">Ou copie o código PIX:</p>
                <Card className="p-3 bg-muted/50">
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs break-all">{pixCode}</code>
                    <Button size="sm" variant="ghost" onClick={copyPixCode} className="shrink-0">
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </Card>
              </div>

              {/* Status */}
              <div className="text-center space-y-2">
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <div className="relative flex items-center justify-center">
                    <div className="w-3 h-3 bg-primary rounded-full animate-ping absolute" />
                    <div className="w-2 h-2 bg-primary rounded-full" />
                  </div>
                  <span>Aguardando pagamento...</span>
                </div>
                <p className="text-xs text-muted-foreground">O pagamento será confirmado automaticamente</p>
                <p className="text-xs font-medium text-primary-foreground">Escaneie o QR Code com seu app bancário</p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
